<template>
	<div class="physical-pos-container">
		<div class="pos-wrapper" v-if="!manualTriggering">
			<PhysicalPos :active-txs="showTransactionsTable" @toggle-transactions-sheet="toggleTransactionsBottomSheet(true)" @send-payment="sendPayment" @changed-terminal="changedTerminal" :no-hide-mobile="noHideMobile" />
		</div>
		<AppBottomSheet :z-index="1020" :forceWidth="bottomSheetWidth" :active="showBottomSheet" @toggling-popup="toggleBottomSheet" >
			<div class="bottom-sheet-content">
				<p class="payment-status">{{ paymentStatus }}</p>
				<p class="payment-status-error" :class="{show: paymentStatusError.length > 0}">{{ paymentStatusError }}</p>
				<PhysicalPosAnimation ref="pos-animation" />
				<div class="payment-actions">
					<div class="payment-action" :class="{notactive: buttonDisabled, pink: !paymentSuccess, green: paymentSuccess}" @click="handleActionButton(false)"><div class="payment-action-inner" v-html="paymentSuccess ? getIcon('iconV') : getIcon('iconReset')"></div></div>
					<div v-if="!!transaction" class="icon-receipt" v-html="getIcon('iconSendReceipt')" @click="showReceiptManager = true"></div>
				</div>
			</div>
		</AppBottomSheet>
		<AppBottomSheet undismissable :z-index="1050" :force-width="bottomSheetWidth" :active="sunmiStore.bluetoothUpdateProgress !== null">
			<ModalHeader
				:title="languageStore.getString('required_update')" />
			<div class="update-required">
				<p class="mt-15 mb-15">{{ languageStore.getString('explain_bluetooth_update') }}</p>
				<AppProgressBar :progress="sunmiStore.bluetoothUpdateProgress" />
			</div>
		</AppBottomSheet>
		<PhysicalPosTransactions :show-transactions-table="showTransactionsTable" ref="pos-transactions" @show-error="showErrorNotification" @toggling-popup="toggleTransactionsBottomSheet" />
		<AppNotification ref="notification" :type="currentNotificationType" :title="currentNotificationTitle" :body-text="currentNotificationText"/>
		<AppNotification ref="refunded-transaction-success-notification" id="refunded-transaction-success" :type="1" :title="languageStore.getString('success')" :body-text="languageStore.getString('transaction_refunded')"/>
		<AppNotification ref="generic-error" :title="languageStore.getString('error')" :body-text="languageStore.getString('generic_error')" :type="-2" />
		<AppNotification ref="nfc-error" :title="languageStore.getString('error')" :body-text="languageStore.getString('fail_nfc_connection')" :type="-2" />
		<AppNotification ref="payment-failed" :title="languageStore.getString('error')" :body-text="languageStore.getString('payment_failed_generic')" :type="-2" />
		<AppNotification ref="tap-phone-not-enabled" :title="languageStore.getString('error')" :body-text="languageStore.getString('tap_on_phone_not_enabled')" :type="-2" :duration="7000" />
		<ReceiptManager :active="showReceiptManager" @toggling-popup="toggleReceiptManager" @success="$refs['receipt-sent'].openNotification()" :item="transaction" />
		<AppNotification :title="languageStore.getString('success')" :body-text="languageStore.getString('email_sent')" ref="receipt-sent" :type="1" />
	</div>
</template>

<script>
import AppButton from "@/shared/components/AppButton.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import TransactionsTable from "@/console-new/components/shared/TransactionsTable.vue";
import PhysicalPos from "@/console-pos/components/PhysicalPos.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppList from "@/shared/components/AppList.vue";
import ReceiptManager from "@/console-pos/components/ReceiptManager.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useUserStore, useAdministrationStore, useCounterStore, useAblyStore, useUnchangingStore} from "@/console-new/stores";
import {useLanguageStore, useSvgStore, useNotificationStore, useCurrencyStore} from "@/shared/stores";

import API from '@/shared/helpers/API.js';
import {useSunmiStore} from "@/console-sun/stores";
import AppProgressBar from "@/shared/components/AppProgressBar.vue";
import {getNative, isAndroid, isIOS, isNative} from "@/shared/helpers/webapp";
import PhysicalPosDailyClose from "@/console-pos/components/PhysicalPosDailyClose.vue";
import PhysicalPosAnimation from "@/console-pos/components/PhysicalPosAnimation.vue";
import PhysicalPosTransactions from "@/console-pos/components/PhysicalPosTransactions.vue";

// Payment state machine states
const PAYMENT_STATES = {
	IDLE: 'idle',
	REQUESTING: 'requesting',
	WAITING: 'waiting',
	SUCCESS: 'success',
	ERROR: 'error',
	CANCELLING: 'cancelling',
	UNKNOWN: 'unknown'
};

// Error types for better parametrization
const ERROR_TYPES = {
	NONE: null,
	GENERIC: 'generic',
	TIMEOUT: 'timeout',
	READER_OFFLINE: 'reader_offline',
	READER_BUSY: 'reader_busy',
	PAYMENT_INTENT: 'payment_intent',
	REACH_READER: 'reader_intent',
	COMMUNICATION: 'api_error',
	CARD_NOT_SUPPORTED: 'card_not_supported',
	INSUFFICIENT_FUNDS: 'insufficient_funds',
	EXPIRED_CARD: 'expired_card',
	PIN_TRY_EXCEEDED: 'pin_try_exceeded',
	INCORRECT_CVC: 'incorrect_cvc',
	DO_NOT_TRY_AGAIN: 'do_not_try_again',
	TRY_AGAIN_LATER: 'try_again_later',
	CONNECTION_ISSUE: 'connection',
	REQUIRES_PIN: 'requires_pin',
	CANCELED: 'canceled',
	CANCEL_FAILED: 'cancel_failed',
	NETWORK_ERROR: 'network_error'
};

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const svgStore = useSvgStore();
		const notificationStore = useNotificationStore();
		const sunmiStore = useSunmiStore();
		const unchangingStore = useUnchangingStore();
		const counterStore = useCounterStore();

		return {languageStore, notificationStore, administrationStore, userStore, getIcon: svgStore.get, sunmiStore, unchangingStore, counterStore};
	},
	name: "PhysicalPosContainer",
	components: {
		PhysicalPosAnimation,
		PhysicalPosDailyClose,
		AppProgressBar,
		PhysicalPosTransactions,
		AppList, AppTextInput, ModalHeader, AppBottomSheet, AppNotification, TransactionsTable, AppButton, PhysicalPos, ReceiptManager},
	mixins: [WindowWidth],
	emits: ['payment-success', 'payment-failed', 'payment-unknown'],
	data() {
		return {
			//ui
			showBottomSheet: false,
			showReceiptManager: false,
			showTransactionsTable: false,
			currentNotificationType: -2,
			currentNotificationTitle: this.languageStore.getString('error'),
			currentNotificationText: this.languageStore.getString('try_later_error'),

			// Payment state with context
			paymentContext: {
				identifier: null,
				state: PAYMENT_STATES.IDLE,
				errorType: ERROR_TYPES.NONE,
				transaction: null,
				terminalId: null,
				orderIds: null,
				posOrderId: null,
			},
			forceResetAfterCancel: false,
			pendingCancellation: false,
			isHandlingCancellation: false,

			//polling
			startTime: null,
			checkInterval: null,
			timeoutTime: 80000,
		}
	},
	props: {
		manualTriggering: {
			type: Boolean,
			default: false
		},
		manualTerminalId: {
			type: String,
			default: null
		},
		noHideMobile: {
			type: Boolean,
			default: false
		},
		noStats: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 330) {
				return "100%";
			} else {
				return "330px";
			}
		},
		isNative() {
			return isNative();
		},
		// Derived states from paymentState
		paymentState() {
			return this.paymentContext.state;
		},
		active() {
			return this.paymentState !== PAYMENT_STATES.IDLE;
		},
		buttonDisabled() {
			return this.paymentState === PAYMENT_STATES.REQUESTING;
		},
		paymentSuccess() {
			return this.paymentState === PAYMENT_STATES.SUCCESS;
		},
		cancelling() {
			return this.paymentState === PAYMENT_STATES.CANCELLING;
		},
		transaction() {
			return this.paymentContext.transaction;
		},
		paymentStatus() {
			switch(this.paymentState) {
				case PAYMENT_STATES.IDLE:
					return "";
				case PAYMENT_STATES.REQUESTING:
					return this.languageStore.getString("requesting_payment");
				case PAYMENT_STATES.WAITING:
					return this.languageStore.getString("waiting_for_payment");
				case PAYMENT_STATES.SUCCESS:
					return this.languageStore.getString("payment_successful");
				case PAYMENT_STATES.ERROR:
					return this.languageStore.getString("payment_failed");
				case PAYMENT_STATES.CANCELLING:
					return this.languageStore.getString("payment_canceling");
				case PAYMENT_STATES.UNKNOWN:
					return this.languageStore.getString("payment_status_unknown");
				default:
					return "";
			}
		},
		paymentStatusError() {
			if (!this.paymentContext.errorType) return "";
			
			switch(this.paymentContext.errorType) {
				case ERROR_TYPES.GENERIC:
					return this.languageStore.getString("payment_failed_generic");
				case ERROR_TYPES.TIMEOUT:
					return this.languageStore.getString("payment_failed_timeout");
				case ERROR_TYPES.READER_OFFLINE:
					return this.languageStore.getString("payment_failed_reader_offline");
				case ERROR_TYPES.READER_BUSY:
					return this.languageStore.getString("payment_failed_reader_busy");
				case ERROR_TYPES.PAYMENT_INTENT:
					return this.languageStore.getString("payment_failed_payment_intent");
				case ERROR_TYPES.REACH_READER:
					return this.languageStore.getString("payment_failed_reach_reader");
				case ERROR_TYPES.COMMUNICATION:
					return this.languageStore.getString("payment_failed_communication_error");
				case ERROR_TYPES.CARD_NOT_SUPPORTED:
					return this.languageStore.getString("payment_failed_card_not_supported");
				case ERROR_TYPES.INSUFFICIENT_FUNDS:
					return this.languageStore.getString("payment_failed_insufficient_funds");
				case ERROR_TYPES.EXPIRED_CARD:
					return this.languageStore.getString("payment_failed_expired_card");
				case ERROR_TYPES.PIN_TRY_EXCEEDED:
					return this.languageStore.getString("payment_failed_pin_try_exceeded");
				case ERROR_TYPES.INCORRECT_CVC:
					return this.languageStore.getString("payment_failed_incorrect_cvc");
				case ERROR_TYPES.DO_NOT_TRY_AGAIN:
					return this.languageStore.getString("payment_failed_do_not_try_again");
				case ERROR_TYPES.TRY_AGAIN_LATER:
					return this.languageStore.getString("payment_failed_try_again");
				case ERROR_TYPES.CONNECTION_ISSUE:
					return this.languageStore.getString("connection_issue");
				case ERROR_TYPES.REQUIRES_PIN:
					return this.languageStore.getString("payment_failed_requires_pin");
				case ERROR_TYPES.CANCELED:
					return this.languageStore.getString("payment_failed_canceled");
				case ERROR_TYPES.CANCEL_FAILED:
					return this.languageStore.getString("payment_cancel_failed");
				case ERROR_TYPES.NETWORK_ERROR:
					return this.languageStore.getString("payment_status_unknown_network");
				default:
					return "";
			}
		}
	},
	methods: {
		// Central state management function
		changeState(newState, errorType = ERROR_TYPES.NONE, transaction = null) {
			const oldState = this.paymentContext.state;
			this.paymentContext.state = newState;
			this.paymentContext.errorType = errorType;
			
			if (transaction !== null) {
				this.paymentContext.transaction = transaction;
			}
			
			// Handle animations based on state transitions
			if (newState === PAYMENT_STATES.REQUESTING || newState === PAYMENT_STATES.WAITING) {
				if(oldState !== PAYMENT_STATES.REQUESTING || this.$refs["pos-animation"].isAnimationPaused()) {
					this.$refs["pos-animation"].playPigWaitingLoop();
				}
			} else if (newState === PAYMENT_STATES.SUCCESS) {
				this.$refs["pos-animation"].playPigPositiveLoop();
			} else if (newState === PAYMENT_STATES.ERROR) {
				this.$refs["pos-animation"].playPigNegativeLoop();
				
				if (errorType === ERROR_TYPES.REQUIRES_PIN || errorType === ERROR_TYPES.GENERIC || errorType === ERROR_TYPES.READER_BUSY) {
					this.$refs["payment-failed"].openNotification();
				}
			} else if (newState === PAYMENT_STATES.UNKNOWN) {
				// Use a waiting animation for UNKNOWN state to indicate system is waiting for verification
				this.$refs["pos-animation"].playPigWaitingLoop();
			} else if (newState === PAYMENT_STATES.IDLE) {
				this.showBottomSheet = false;
				this.$refs["pos-animation"].pauseAnimation();
			}
		},
		
		// Centralized method for cancelling payments in any state
		cancelPayment(silent = false, callback = null) {
			// Handle different states
			switch (this.paymentState) {
				case PAYMENT_STATES.SUCCESS:
					// No need to cancel successful payments
					if (callback) callback();
					return;

				case PAYMENT_STATES.ERROR:

					// If in ERROR state, we can reset the payment state
					this.resetPaymentState();
					if (callback) callback();
					return;

				case PAYMENT_STATES.IDLE:
					// Check if there's a pending cancellation from a previous REQUESTING state
					if (this.pendingCancellation && this.paymentContext.terminalId) {
						this.pendingCancellation = false;

						// For API-based terminals, attempt to cancel the pending payment
						if (this.paymentContext.terminalId === "bluetooth" && isNative()) {
							getNative().cancelCurrentAction();
						} else if (!this.paymentContext.terminalId.startsWith("pax") && !this.paymentContext.terminalId.startsWith("dojo")) {

							const identifier = this.paymentContext.identifier;
							API.init().cancelTerminalPayment(this.userStore.currBusinessId, this.paymentContext.terminalId, this.paymentContext.posOrderId, () => {
								if (identifier !== this.paymentContext.identifier) {
									return;
								}
								this.resetPaymentState();
								if (callback) callback();
							}, () => {
								this.resetPaymentState();
								if (callback) callback();
							});
							return;
						}
					} else if(this.paymentContext.terminalId && this.paymentContext.terminalId.startsWith("dojo")) {
						const identifier = this.paymentContext.identifier;
						API.init().cancelTerminalPayment(this.userStore.currBusinessId, this.paymentContext.terminalId, this.paymentContext.posOrderId, () => {
							if (identifier !== this.paymentContext.identifier) {
								return;
							}
							this.resetPaymentState();
							if (callback) callback();
						}, () => {
							this.resetPaymentState();
							if (callback) callback();
						});
						return;
					}
					
					// Regular IDLE/ERROR handling
					if (callback) callback();
					return;
					
				case PAYMENT_STATES.CANCELLING:
					// Already cancelling, just set flag for later use
					this.forceResetAfterCancel = true;
					if (callback) callback();
					return;
					
				case PAYMENT_STATES.REQUESTING:
					// Payment is still being requested, can't cancel via API yet
					this.pendingCancellation = true;

					if (callback) callback();
					return;
					
				case PAYMENT_STATES.WAITING:
				case PAYMENT_STATES.UNKNOWN:
					// Can cancel these states via API
					if (!this.paymentContext.terminalId) {
						if (callback) callback();
						this.resetPaymentState();
						return;
					}
					
					// Only cancel via API for supported terminals
					if (this.paymentContext.terminalId === "bluetooth" && isNative()) {
						getNative().cancelCurrentAction();
						if (!silent) {
							this.resetPaymentState();
						}

						if (callback) callback();
					} else if (!this.paymentContext.terminalId.startsWith("pax")) {
						this.changeState(PAYMENT_STATES.CANCELLING);
						
						const identifier = this.paymentContext.identifier;
						API.init().cancelTerminalPayment(this.userStore.currBusinessId, this.paymentContext.terminalId, this.paymentContext.posOrderId, (r) => {

							if (identifier !== this.paymentContext.identifier) {
								return;
							}

							if (callback) callback();
							
							if (r && r.result === "OK") {
								if (!silent || this.forceResetAfterCancel) {
									this.resetPaymentState();
								}
							} else {
								if (silent) {
									this.changeState(PAYMENT_STATES.ERROR, ERROR_TYPES.CANCEL_FAILED);
								} else {
									this.resetPaymentState();
								}
							}
						}, () => {
							if (callback) callback();
							this.resetPaymentState();
						});
					} else {
						if (!silent) {
							this.resetPaymentState();
						}
						
						if (callback) callback();
					}
					return;
			}
		},
		
		// Reset all state completely (used when cancellation completes or when payment is successful)
		resetPaymentState() {

			this.pendingCancellation = false;
			this.isHandlingCancellation = false;
			this.forceResetAfterCancel = false;
			this.startTime = null;

			this.counterStore.externalPaymentIntentEnd("KO");
			
			this.changeState(PAYMENT_STATES.IDLE);
		},
		
		showErrorNotification(message) {
			this.currentNotificationType = -2;
			this.currentNotificationTitle = this.languageStore.getString('error');
			this.currentNotificationText = message;
			this.$refs["notification"].openNotification();
		},
		
		toggleReceiptManager(isOpen) {
			if(!isOpen) {
				this.showReceiptManager = false;
			}
		},
		
		toggleBottomSheet(isOpen) {
			if(!isOpen) {
				this.showBottomSheet = false;

				if (this.counterStore.externalPaymentIntentData) {
					this.counterStore.externalPaymentIntentEnd("KO");
					return;
				}

				// Prevent recursive calls
				if (this.isHandlingCancellation) {
					return;
				}

				// Ensure animation is reset when sheet is closed
				this.$refs["pos-animation"].pauseAnimation();

				let paymentType = this.getPaymentTypeFromContext();
				// If we're in SUCCESS state, emit the payment-success event before resetting
				if (this.paymentState === PAYMENT_STATES.SUCCESS) {
					this.$emit('payment-success', this.paymentContext.orderIds, paymentType);
					this.resetPaymentState();
					return;
				}
				
				// Use the centralized cancelPayment method
				this.isHandlingCancellation = true;
				this.cancelPayment(false, () => {
					// Reset the flag when complete
					this.isHandlingCancellation = false;
					// We don't call resetPaymentState here as cancelPayment handles state changes
				});
			}
		},
		getPaymentTypeFromContext() {
			let terminalId = this.paymentContext?.terminalId;
			if(terminalId && terminalId?.startsWith && (terminalId?.startsWith('dojo') || terminalId?.startsWith('pax'))) {
				return 'external';
			}
			return 'stripe';
		},
		toggleTransactionsBottomSheet(isOpen) {
			this.showTransactionsTable = isOpen;
		},
		
		changedTerminal(terminalId) {
			if(terminalId) {
				this.paymentContext.terminalId = terminalId;
			}
		},
		
		sendPayment(amount, terminalId, orderIds = null, itemsRedeemed = null, dutchPeople = null, chosenPaymentMode = null, discountOnTotal = null, discountPartial = null, appliedCoupons = null, skipTipping = false, printSingleItem = false) {

			if(this.paymentContext.terminalId !== terminalId) {
				this.changedTerminal(terminalId);
			}
			
			this.$refs["pos-animation"].loadAnimationPig(terminalId);
			this.pendingCancellation = false;
			this.isHandlingCancellation = false;
			this.showBottomSheet = true;
			this.paymentContext.orderIds = orderIds;
			this.paymentContext.posOrderId = null;
			this.paymentContext.identifier = terminalId+Math.random();
			this.paymentContext.transaction = null;
			this.changeState(PAYMENT_STATES.REQUESTING);

			const currencyStore = useCurrencyStore();
			if(amount < currencyStore.currMinInteger) {
				let formattedMinAmount = currencyStore.readableCurrencyValue(currencyStore.currCurrency.min_stripe_amount);
				this.notificationStore.showNotification("total-too-low", [formattedMinAmount]);
				this.changeState(PAYMENT_STATES.IDLE);
				return;
			}

			if(!this.userStore.currBusiness.config_payments) {
				this.notificationStore.showNotification("stripe-payments-not-active");
				this.changeState(PAYMENT_STATES.IDLE);
				return;
			}

			const counterStore = useCounterStore();
			let invoiceData = null;
			if (counterStore.selectedCustomer !== null) {
				invoiceData = {
					customer_id: counterStore.selectedCustomer.customer_id,
					customer_type: counterStore.selectedCustomer.type,
				};
			}

			if(terminalId === "tap_phone") {
				this.localTerminalPayment(amount, "tap_on_phone", orderIds, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, printSingleItem);
				return;
			} else if(terminalId === "bluetooth") {
				this.localTerminalPayment(amount, "bluetooth", orderIds, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, printSingleItem);
				return;
			} else if(terminalId === "handoff") {
				this.localTerminalPayment(amount, "handoff", orderIds, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, printSingleItem);
				return;
			}

			if(invoiceData && invoiceData?.customer_type === 'company' && counterStore?.invoiceAdditionalData?.purchase_order) {
				invoiceData.purchase_order = counterStore.invoiceAdditionalData.purchase_order;
			}

			const identifier = this.paymentContext.identifier;

			API.init().payTerminal(this.userStore.currBusinessId, terminalId, amount, this.noStats, orderIds, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, skipTipping, printSingleItem, (r) => {
				// Check if we're still in REQUESTING state - if not, the user may have closed the sheet
				if (this.paymentState !== PAYMENT_STATES.REQUESTING) {
					return;
				}

				if (identifier !== this.paymentContext.identifier) {
					return;
				}
				
				// Check if we have a pending cancellation - process it before starting polling
				if (this.pendingCancellation) {
					this.pendingCancellation = false;
					
					// Cancel the payment immediately via API
					if (r && r.result === "OK" && r.pos_order_id) {

						this.paymentContext.posOrderId = r.pos_order_id;

						const identifier = this.paymentContext.identifier;
						API.init().cancelTerminalPayment(this.userStore.currBusinessId, terminalId, this.paymentContext.posOrderId, () => {
							if (identifier !== this.paymentContext.identifier) {
								return;
							}
							this.changeState(PAYMENT_STATES.IDLE);
						}, () => {
							this.changeState(PAYMENT_STATES.IDLE);
						});
					} else {
						// No valid pos_order_id received, just reset state
						this.changeState(PAYMENT_STATES.IDLE);
					}
					return;
				}
				
				if(r && r.result === "OK" && r.pos_order_id) {
					this.changeState(PAYMENT_STATES.WAITING);
					this.paymentContext.posOrderId = r.pos_order_id;
					
					this.startChannelSub(this.paymentContext.posOrderId);
				} else {
					let errorType = ERROR_TYPES.GENERIC;
					
					if(r.details) {
						if(r.details === "terminal_offline") {
							errorType = ERROR_TYPES.READER_OFFLINE;
						} else if(r.details === "dojo_terminal_busy") {
							errorType = ERROR_TYPES.READER_BUSY;
						} else if(r.details === "payment_intent_error" || r.details === "dojo_session_error" || r.details === "dojo_payment_intent_error") {
							errorType = ERROR_TYPES.PAYMENT_INTENT;
						} else if(r.details === "reader_intent_error") {
							errorType = ERROR_TYPES.REACH_READER;
						} else if(r.details === "api_error") {
							errorType = ERROR_TYPES.COMMUNICATION;
						}
					}

					this.changeState(PAYMENT_STATES.ERROR, errorType);
				}
			}, () => {
				// Only change state if we're still in requesting state
				if (this.paymentState === PAYMENT_STATES.REQUESTING) {
					this.changeState(PAYMENT_STATES.ERROR, ERROR_TYPES.GENERIC);
				}
			});
		},
		
		localTerminalPayment(amount, type, orderIds = null, chosenPaymentMode = null, itemsRedeemed = null, dutchPeople = null, invoiceData = null, discountOnTotal = null, discountPartial = null, appliedCoupons = null, printSingleItem = false) {
			if(isNative() && getNative().payWithLocalTerminal) {
				let refreshToken = localStorage.getItem("refresh_token");
				if (refreshToken) {

					const identifier = this.paymentContext.identifier;

					this.userStore.getBusinessLocation((locationId) => {
						// Check if we're still in REQUESTING state
						if (this.paymentState !== PAYMENT_STATES.REQUESTING) {
							return;
						}

						if (identifier !== this.paymentContext.identifier) {
							return;
						}
						
						if(locationId) {
							API.init().payTerminalLocal(this.userStore.currBusinessId, amount, this.noStats, null, orderIds, type, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, (r) => {
								// Check again if we're still in REQUESTING state
								if (this.paymentState !== PAYMENT_STATES.REQUESTING) {
									return;
								}

								if (identifier !== this.paymentContext.identifier) {
									return;
								}
								
								if(r && r.result === "OK" && r.client_secret) {

									if(!this.pendingCancellation) {
										this.changeState(PAYMENT_STATES.WAITING);
										this.paymentContext.posOrderId = r.pos_order_id;
										this.startChannelSub(this.paymentContext.posOrderId);

										if(isAndroid()) {
											getNative().payWithLocalTerminal(r.client_secret, type, locationId, refreshToken, this.userStore.currBusinessId);
										} else if(isIOS()) {
											getNative().payWithLocalTerminalV2(r.client_secret, type, this.userStore.currBusiness.stripe_account, locationId, refreshToken, this.userStore.currBusinessId);
										}
									} else {
										this.resetPaymentState();
									}

								} else {
									if(r.details && r.details === "tap_on_phone_not_supported") {
										this.$refs["tap-phone-not-enabled"].openNotification();
									} else {
										this.$refs["generic-error"].openNotification();
									}
								}
							})
						} else {
							this.$refs["generic-error"].openNotification();
						}
					});
				}
			}
		},
		channelListener(message) {

			if(Number(message.data.pos_order_id) !== Number(this.paymentContext.posOrderId)) {
				return;
			}

			if (this.paymentState === PAYMENT_STATES.ERROR || this.paymentState === PAYMENT_STATES.CANCELLING) {
				if(this.checkInterval) {
					clearTimeout(this.checkInterval);
					this.checkInterval = null;
				}
				return;
			}

			// Check if there's a pending cancellation request
			if (this.pendingCancellation) {
				this.pendingCancellation = false; // Reset the flag
				this.cancelPayment(false); // Perform the actual cancellation
				return;
			}

			if(message.name === "confirmed") {

				this.changeState(PAYMENT_STATES.SUCCESS, ERROR_TYPES.NONE);
				this.displayTransactionReceipt();
				this.stopChannelSub(this.paymentContext.posOrderId);

			} else if(message.name === "failed") {

				let errorType = ERROR_TYPES.GENERIC;

				if(message.data.decline_code) {
					const declineCode = message.data.decline_code;
					if(declineCode === "card_not_supported" || declineCode === "currency_not_supported") {
						errorType = ERROR_TYPES.CARD_NOT_SUPPORTED;
					} else if(declineCode === "card_velocity_exceeded" || declineCode === "insufficient_funds") {
						errorType = ERROR_TYPES.INSUFFICIENT_FUNDS;
					} else if(declineCode === "expired_card") {
						errorType = ERROR_TYPES.EXPIRED_CARD;
					} else if(declineCode === "pin_try_exceeded") {
						errorType = ERROR_TYPES.PIN_TRY_EXCEEDED;
					} else if(declineCode === "incorrect_cvc" || declineCode === "incorrect_pin") {
						errorType = ERROR_TYPES.INCORRECT_CVC;
					} else if(declineCode === "do_not_try_again" || declineCode === "fraudulent" || declineCode === "lost_card" || declineCode === "stolen_card" || declineCode === "merchant_blacklist") {
						errorType = ERROR_TYPES.DO_NOT_TRY_AGAIN;
					} else if(declineCode === "try_again_later" || declineCode === "processing_error") {
						errorType = ERROR_TYPES.TRY_AGAIN_LATER;
					}
				}

				this.changeState(PAYMENT_STATES.ERROR, errorType);
				this.stopChannelSub(this.paymentContext.posOrderId);
			}

		},
		startChannelSub(posOrderId) {
			// Safety check - only start polling if in WAITING state
			if (this.paymentState !== PAYMENT_STATES.WAITING) {
				return;
			}

			this.startTime = new Date().getTime();


			const channelKey = ENVIRONMENT+":pos-order:"+this.userStore.currBusinessId+":"+posOrderId;

			const ablyStore = useAblyStore();
			ablyStore.startAbly(() => {
				ablyStore.subscribeToChannel(channelKey, this.channelListener);
			});

		},
		stopChannelSub(posOrderId) {
			const channelKey = ENVIRONMENT+":pos-order:"+this.userStore.currBusinessId+":"+posOrderId;

			const ablyStore = useAblyStore();
			ablyStore.unsubscribeFromChannel(channelKey);

		},

		handleActionButton(failedMessage = true, callback = null) {
			// If in success state, this button acts as confirmation
			if (this.paymentState === PAYMENT_STATES.SUCCESS) {
				let paymentType = this.getPaymentTypeFromContext();
				// Emit payment success event when user clicks the confirmation button
				this.$emit('payment-success', this.paymentContext.orderIds, paymentType);
				this.resetPaymentState();
				return;
			}

			this.cancelPayment(failedMessage, callback);
		},

		displayTransactionReceipt() {
			const identifier = this.paymentContext.identifier;
			setTimeout(() => {
				API.init().getPosOrder(this.userStore.currBusinessId, this.paymentContext.terminalId, this.paymentContext.posOrderId, (r) => {

					if(identifier !== this.paymentContext.identifier) {
						return;
					}

					if(r && r.result === "OK" && r.pos_order) {
						this.paymentContext.transaction = r.pos_order.transaction || null;
					} else {
						this.paymentContext.transaction = null;
					}

				}, () => {
					if(identifier !== this.paymentContext.identifier) {
						return;
					}
					this.paymentContext.transaction = null;
				});
			}, 1000); // Delay to wait for stripe webhook to finish executing (spoiler: we should speed it up)
		},
	},
	mounted() {
		this.$refs["pos-animation"].loadAnimationPig(this.paymentContext.terminalId);
		this.notificationStore.notifications.push(this.$refs['refunded-transaction-success-notification']);

		if(this.manualTriggering && this.manualTerminalId) {
			this.paymentContext.terminalId = this.manualTerminalId;
		}

	},
	beforeUnmount() {
		if(this.checkInterval !== null) {
			clearTimeout(this.checkInterval);
		}
	},
	watch: {
		'sunmiStore.failedTapPhoneConnection'(newValue, oldValue) {
			if(oldValue !== newValue && newValue) {

				this.sunmiStore.failedTapPhoneConnection = false;
				if(this.paymentState === PAYMENT_STATES.IDLE) {
					return;
				}
				this.$refs["nfc-error"].openNotification();
				this.$emit('payment-failed');
			}
		},
		'sunmiStore.failedTapPhonePayment'(newValue, oldValue) {
			if(oldValue !== newValue && newValue) {

				this.sunmiStore.failedTapPhonePayment = false;
				if(this.paymentState === PAYMENT_STATES.IDLE) {
					return;
				}

				let errorType = ERROR_TYPES.GENERIC;
				
				if(newValue === "canceled") {
					errorType = ERROR_TYPES.CANCELED;
				} else if(newValue === "connection") {
					errorType = ERROR_TYPES.CONNECTION_ISSUE;
				} else if(newValue === "requires_pin") {
					errorType = ERROR_TYPES.REQUIRES_PIN;
				} else if(newValue === "collect_payment_method") {
					errorType = ERROR_TYPES.CANCELED;
				}

				this.changeState(PAYMENT_STATES.ERROR, errorType);

			}
		},
		'sunmiStore.successTapPhoneConnection'(newValue, oldValue) {
			if(oldValue !== newValue && newValue) {
				this.sunmiStore.successTapPhoneConnection = false;

				// We use ably to do this: this.changeState(PAYMENT_STATES.SUCCESS);

			}
		},
		'unchangingStore.offline'(newValue, oldValue) {
			if(oldValue !== newValue) {
				if(newValue) {
					if(this.paymentState === PAYMENT_STATES.REQUESTING || this.paymentState === PAYMENT_STATES.WAITING) {
						this.changeState(PAYMENT_STATES.UNKNOWN, ERROR_TYPES.NETWORK_ERROR);
					}
				} else {
					if(this.paymentState === PAYMENT_STATES.UNKNOWN && this.paymentContext.terminalId) {
						this.changeState(PAYMENT_STATES.WAITING);
					}
				}
			}
		},
	}
}
</script>

<style lang="scss" scoped>

.bottom-sheet-content {
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;

	.animation-container {
		background-color: var(--color-alpha-1);
		box-shadow: inset 2px 1px 7px var(--color-box-shadow-3);
		border-radius: 13px;
		display: flex;
		align-items: center;
		height: 300px;
		overflow: hidden;

		.pig-animation {
			width: 100%;
			max-width: 300px;
		}

	}

	.payment-status {
		color: var(--color-primary);
		font-size: 22px;
		text-align: center;
		font-weight: 700;
		max-width: 300px;
	}

	.payment-status-error {
		color: var(--color-danger);
		font-size: 15px;
		text-align: center;
		padding: 0;
		max-width: 300px;
		max-height: 0;
		transition: max-height 0.5s ease;
		overflow: hidden;
		margin: 10px 0;

		&.show {
			max-height: 300px;
		}

	}


	.payment-actions {
		position: relative;
		display: flex;
		flex-direction: row;
		justify-content: space-around;
		margin: 20px 0;
		width: 100%;

		.payment-action {
			background-color: #2352A2;
			color: white;
			border-radius: 40px;
			width: 80px;
			height: 80px;
			display: flex;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			transition: background-color 0.3s ease;

			.payment-action-inner {
				background-color: #2352A2;
				border-radius: 40px;
				box-shadow: -3px -3px 6px rgba(255, 255, 255, 0.25), inset -3px -3px 6px rgba(255, 255, 255, 0), 3px 3px 6px rgba(0, 0, 0, 0.25), inset 3px 3px 6px rgba(0, 0, 0, 0);
				font-size: 40px;
				display: flex;
				justify-content: center;
				align-items: center;
				width: 70%;
				height: 70%;
				transition: box-shadow 0.1s ease-in-out, background-color 0.3s ease;
				@include noSelect();

				:deep(svg) {
					width: 55%;
					height: 55%;

					.fill {
						transition: fill 0.1s ease;
						fill: white;
					}

					.stroke {
						transition: stroke 0.1s ease;
						stroke: white;
					}
				}

			}

			&:active .payment-action-inner {
				box-shadow: -3px -3px 6px rgba(255, 255, 255, 0), inset -3px -3px 6px rgba(255, 255, 255, 0.25), 3px 3px 6px rgba(0, 0, 0, 0), inset 3px 3px 6px rgba(0, 0, 0, 0.25);
			}

			&.yellow {
				background-color: #FDC700;
				color: white;

				.payment-action-inner {
					background-color: #FDC700;
				}
			}

			&.pink {
				background-color: #FC2291;

				.payment-action-inner {
					background-color: #FC2291;
				}
			}

			&.green {
				background-color: $secondary;
				color: $primary;

				.payment-action-inner {
					background-color: $secondary;
				}
			}

			&.notactive {
				background-color: #ADBFDC;
				cursor: default;
				pointer-events: none;

				.payment-action-inner {
					background-color: #ADBFDC;
					box-shadow: -3px -3px 6px rgba(255, 255, 255, 0), inset -3px -3px 6px rgba(255, 255, 255, 0), 3px 3px 6px rgba(0, 0, 0, 0), inset 3px 3px 6px rgba(0, 0, 0, 0);
				}

				:deep(svg) {

					.fill {
						fill: $primary;
					}

					.stroke {
						stroke: $primary;
					}
				}
			}
		}

		.icon-receipt {
			width: 20px;
			height: 20px;
			padding: 8px;
			position: absolute;
			top: calc(50% - 16px);
			right: 0;
			cursor: pointer;
			box-sizing: content-box;

			:deep(svg) {
				width: 100%;
				height: 100%;

				.fill {
					fill: $primary;
				}

				.stroke {
					stroke: $primary;
				}

			}
		}
	}
}
</style>